/**
 * platform-company.ts
 * Shared Zod contracts for platform-admin company management — KI-12.1, KI-12.2
 *
 * Covers:
 *  - Platform login request/response
 *  - Company list and single-company response shapes
 *  - Create company request/response
 *  - Patch company request
 *  - Module entitlement set request/response (Phase 1c)
 *
 * ZERO Hono/Drizzle/infrastructure imports — Zod only.
 *
 * Spec: KI-12.1, KI-12.2, KI-12-A, KI-12-B, PC-6 (Phase 1c module entitlement)
 */
import { z } from 'zod';
import { SignInMethodSchema } from './sign-in-method.js';

// ---------------------------------------------------------------------------
// Module entitlement keys — the four product modules (no admin, no sub-modules)
// These match the entitlement flag suffix: module.<key>.enabled in catalog.feature_flag
// ---------------------------------------------------------------------------

/**
 * All valid product-module entitlement keys.
 * Order matches MODULE_ENTITLEMENT_KEYS in auth-me.routes.ts.
 * Renamed compras->requi, aplicativos->app (SDD-roles.md §1, migration 0154) —
 * 'requi' now matches the frontend MODULE_REGISTRY UI key exactly, no bridge.
 *
 * 'cuentas_pagar' (added by add-cuentas-pagar-ui, design D4) is RETIRED by
 * reorganizar-cuentas-pagar-bajo-sumi (2026-10-05, design D9) — AP (Cuentas a
 * Pagar) is a SECTION of the SUMI module now, gated by `module.sumi.enabled`
 * + its own `sumi.*` capabilities; it is no longer its own commercial
 * entitlement.
 */
export const PRODUCT_MODULE_KEYS = ['locx', 'requi', 'nomi', 'sumi', 'app'] as const;
export type ProductModuleKey = (typeof PRODUCT_MODULE_KEYS)[number];

// ---------------------------------------------------------------------------
// Platform login
// ---------------------------------------------------------------------------

export const PlatformLoginBodySchema = z.object({
  email: z.string().email('email must be a valid email address'),
  password: z.string().min(1, 'password is required'),
  // NOTE: NO companyId field — platform users are global, not tenant-scoped (KI-9.7).
});

export const PlatformLoginResponseSchema = z.object({
  token: z.string(),
});

export type PlatformLoginBody = z.infer<typeof PlatformLoginBodySchema>;
export type PlatformLoginResponse = z.infer<typeof PlatformLoginResponseSchema>;

// ---------------------------------------------------------------------------
// CUIT validator (CP-2)
// Argentine CUIT format: NN-NNNNNNNN-N
// ---------------------------------------------------------------------------

/**
 * Optional CUIT field validator.
 * When present, must match pattern ^\d{2}-\d{8}-\d{1}$.
 * Returns 422 VALIDATION_ERROR when invalid (enforced at Zod layer per CP-2).
 */
export const CuitSchema = z
  .string()
  .regex(/^\d{2}-\d{8}-\d{1}$/, 'cuit must match pattern NN-NNNNNNNN-N (e.g. 30-12345678-9)')
  .optional();

// ---------------------------------------------------------------------------
// Company shapes
// ---------------------------------------------------------------------------

/**
 * The `identity.organization` row linked to a platform company (`linkedCompanyId`
 * resolves back to the company's id) — kernel-contractor-directory CD6.
 * Only `id`/`legalName`/`cuit` are surfaced: enough for a platform admin to
 * recognize WHICH organization is linked, without exposing the rest of the
 * organization row.
 */
export const LinkedOrganizationSchema = z.object({
  id: z.string().uuid(),
  legalName: z.string(),
  cuit: z.string().nullable(),
});

export type LinkedOrganization = z.infer<typeof LinkedOrganizationSchema>;

/** Full company shape returned by GET /platform/companies (includes createdAt + entitledModules). */
export const PlatformCompanySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Human-readable login code (e.g. "acme"). Used by POST /auth/login. */
  slug: z.string(),
  active: z.boolean(),
  /** ISO 8601 date string — backend aliases `created_at AS "createdAt"` (design §5). */
  createdAt: z.string(),
  /**
   * Currently entitled product-module keys for this company.
   * Resolved as: override.value if present, else feature_flag.default_value.
   * Keys are: 'locx' | 'requi' | 'nomi' | 'sumi' | 'app'.
   * Default: [] — callers that don't populate this field get an empty array.
   */
  entitledModules: z.array(z.string()).default([]),
  /**
   * The `identity.organization` row (if any) whose `linkedCompanyId` points
   * back to this company — kernel-contractor-directory CD6. `null` when no
   * organization is linked yet. Batched read, no RLS involved (`organization`
   * has none).
   */
  linkedOrganization: LinkedOrganizationSchema.nullable().default(null),
  /**
   * Sum of the monthly USD price of the modules the company has contracted
   * (enabled); un-priced modules count as 0 (platform-company-hub T1).
   */
  monthlyTotalUsd: z.number().default(0),
});

/**
 * Company detail shape returned by GET /platform/companies/:id (CP-3).
 * Extends the list shape with profile fields.
 */
export const PlatformCompanyDetailSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  active: z.boolean(),
  cuit: z.string().nullable(),
  phone: z.string().nullable(),
  logo_file_id: z.string().uuid().nullable(),
  /** Dark-mode logo variant (CP-LOGO-DARK). Independently nullable from logo_file_id. */
  logo_file_id_dark: z.string().uuid().nullable(),
  /** How the company's users sign in (identity.company.sign_in_method). */
  sign_in_method: SignInMethodSchema,
  created_at: z.string(),
  updated_at: z.string(),
});

/** GET /platform/companies response — wrapped object, not a bare array. */
export const CompanyListResponseSchema = z.object({
  companies: z.array(PlatformCompanySchema),
});

/**
 * PATCH /platform/companies/:id response — company without createdAt.
 * The UPDATE RETURNING clause returns only id, name, active.
 */
export const CompanyResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  active: z.boolean(),
});

export type PlatformCompany = z.infer<typeof PlatformCompanySchema>;
export type PlatformCompanyDetail = z.infer<typeof PlatformCompanyDetailSchema>;
export type CompanyListResponse = z.infer<typeof CompanyListResponseSchema>;
export type CompanyResponse = z.infer<typeof CompanyResponseSchema>;

// ---------------------------------------------------------------------------
// Create company
// ---------------------------------------------------------------------------

export const CreateCompanyBodySchema = z.object({
  name: z.string().min(1, 'name is required'),
  adminEmail: z.string().email('adminEmail must be a valid email address'),
  adminPassword: z.string().min(8, 'adminPassword must be at least 8 characters'),
  /**
   * Optional custom slug. If omitted, the backend derives one from the company name.
   * Must be lowercase alphanumeric + hyphens, 1-100 chars.
   * The resolved slug is returned in the response so operators know the login code.
   */
  slug: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9-]+$/, 'slug must contain only lowercase letters, digits, and hyphens')
    .optional(),
});

export const CreateCompanyResponseSchema = z.object({
  companyId: z.string().uuid(),
  adminEmail: z.string().email(),
  /** The resolved login slug for this company — tell operators to share this with their users. */
  slug: z.string(),
});

export type CreateCompanyBody = z.infer<typeof CreateCompanyBodySchema>;
export type CreateCompanyResponse = z.infer<typeof CreateCompanyResponseSchema>;

// ---------------------------------------------------------------------------
// Patch company
// ---------------------------------------------------------------------------

/** Toggle-active body — existing contract (KI-10.5–10.8). */
export const PatchCompanyBodySchema = z.object({
  active: z.boolean({
    required_error: 'active is required',
    invalid_type_error: 'active must be a boolean',
  }),
});

export type PatchCompanyBody = z.infer<typeof PatchCompanyBodySchema>;

/**
 * Edit-fields body — CP-4/5.
 * Accepts a partial { name?, cuit?, phone?, sign_in_method? }; all fields optional.
 * When sent instead of { active }, the PATCH handler updates the profile fields.
 */
export const PatchCompanyFieldsBodySchema = z.object({
  name: z.string().min(1, 'name must not be empty').optional(),
  cuit: CuitSchema,
  phone: z.string().optional(),
  sign_in_method: SignInMethodSchema.optional(),
});

export type PatchCompanyFieldsBody = z.infer<typeof PatchCompanyFieldsBodySchema>;

// ---------------------------------------------------------------------------
// Company users — GET /platform/companies/:id/users (CP-11/12)
// ---------------------------------------------------------------------------

/** Single user row in the company users list response. */
export const CompanyUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  active: z.boolean(),
  created_at: z.string(),
  roles: z.array(z.string()),
});

/** Response array for GET /platform/companies/:id/users. Sorted by email asc. */
export const CompanyUsersResponseSchema = z.array(CompanyUserSchema);

export type CompanyUser = z.infer<typeof CompanyUserSchema>;
export type CompanyUsersResponse = z.infer<typeof CompanyUsersResponseSchema>;

// ---------------------------------------------------------------------------
// Logo upload — POST /platform/companies/:id/logo (CP-LOGO)
// ---------------------------------------------------------------------------

/**
 * Response for POST /platform/companies/:id/logo.
 * Returns the file_id inserted into identity.file.
 */
export const UploadLogoResponseSchema = z.object({
  file_id: z.string().uuid(),
});

export type UploadLogoResponse = z.infer<typeof UploadLogoResponseSchema>;

// ---------------------------------------------------------------------------
// Module entitlement set — PUT /platform/companies/:id/modules (Phase 1c)
// ---------------------------------------------------------------------------

/**
 * Request body for PUT /platform/companies/:id/modules.
 * `modules` is the full set of enabled product-module keys.
 * Any key not in this list gets an explicit override of false.
 * (Explicit false is required for locx because its default is true.)
 */
export const SetModuleEntitlementsBodySchema = z.object({
  modules: z.array(
    z.enum(PRODUCT_MODULE_KEYS),
    {
      required_error: 'modules is required',
      invalid_type_error: 'modules must be an array of module keys',
    },
  ),
});

/**
 * Response for PUT /platform/companies/:id/modules.
 * Returns the full set of entitled module keys after the update.
 */
export const SetModuleEntitlementsResponseSchema = z.object({
  companyId: z.string().uuid(),
  entitledModules: z.array(z.string()),
});

export type SetModuleEntitlementsBody = z.infer<typeof SetModuleEntitlementsBodySchema>;
export type SetModuleEntitlementsResponse = z.infer<typeof SetModuleEntitlementsResponseSchema>;
